import { computed, ComputedRef } from "vue";
import { useUserQuery } from "../services/graphql/generated/consumer-graph-types";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessAction, AccessFeature, AccessResource } from "@/shared/access-control/access-control.ts";

/**
 * Composable that resolves whether the current user has access to the AI Assistant feature.
 *
 * Access rules (evaluated in order):
 * 1. `rag_rbac` disabled → always granted
 * 2. Anonymous user     → requires `rag_anonymous` feature
 * 3. `rag` resource present → requires `rag:write` resource permission
 * 4. Otherwise           → denied
 *
 * @returns {{ hasAccess: ComputedRef<boolean> }} Reactive flag indicating access.
 */
export const useAiAssistantAccess = (): { hasAccess: ComputedRef<boolean> } => {
    const { result: user } = useUserQuery();

    const { hasAccess: hasRagGuiFeature } = useHasAccess({ featureID: AccessFeature.rag_gui });
    const { hasAccess: hasRagRbacFeature } = useHasAccess({ featureID: AccessFeature.rag_rbac });
    const { hasAccess: hasRagAnonymousAccess } = useHasAccess({ featureID: AccessFeature.rag_anonymous });
    const { hasAccess: hasResourceAccess } = useHasAccess({
        resource: AccessResource.rag,
        action: AccessAction.write,
    });

    /** `true` when the current user session is anonymous (not authenticated). */
    const isAnonymous = computed(() => !!user?.value?.me.anonymous);

    /**
     * Reactive access flag. Evaluates all relevant feature flags and permissions
     * according to the priority rules described on {@link useAiAssistantAccess}.
     */
    const hasAccess = computed(() => {
        if (!hasRagGuiFeature.value) return false;
        if (!hasRagRbacFeature.value) return true;
        if (isAnonymous.value) return hasRagAnonymousAccess.value;

        return hasResourceAccess.value;
    });

    return { hasAccess };
};
